import { useCallback, useLayoutEffect } from 'react'

import type { DateType } from '@/utils/date'
import type { DateRange } from 'react-day-picker'
import type { BranchType, DateContextType } from './filterSlice'
import {
  getDateContextForPage,
  initializeDateContext,
  resetFilters,
  selectActiveDateContext,
  selectChartType,
  selectComparisonBranch,
  selectDateContexts,
  selectFilterState,
  selectIsActiveContextInitialized,
  selectPageRefreshTrigger,
  selectRefreshCallbacks,
  selectSelectedBranch,
  selectSelectedBranchType,
  selectSizeFilter,
  selectTemperatureFilter,
  selectTypeFilter,
  setActiveDateContext,
  setChartType,
  setComparisonBranch,
  setDateFilterTypeForContext,
  setDateForContext,
  setSelectedBranch,
  setSelectedBranchType,
  setSizeFilter,
  setTemperatureFilter,
  setTypeFilter,
  triggerRefresh,
} from './filterSlice'
import {
  getDefaultDateFilterTypeForContext,
  getDefaultDateForContext,
} from './filterDefaults'
import { useAppDispatch, useAppSelector } from './hooks'
import { selectTenant } from './tenantSlice'

// ============================================================================
// Initialization Hook
// ============================================================================

/**
 * Hook to initialize date context for a page.
 *
 * Uses `useLayoutEffect` to synchronously set the active context BEFORE
 * the component renders, preventing stale state issues during navigation.
 *
 * @param pageId - The page identifier (e.g., 'sales', 'spend', 'profit-and-loss')
 * @returns Object containing:
 *   - isReady: boolean - True when the context is initialized and safe to fetch data
 *   - context: DateContextType - The active date context ('monthly' | 'quarterly')
 *
 * @example
 * ```tsx
 * const SpendPage = () => {
 *   const { isReady, context } = useInitializeDateContext('spend')
 *   const { date, dateFilterType } = useReduxBaseFilter()
 *
 *   // Only fetch data when context is ready
 *   useEffect(() => {
 *     if (isReady) {
 *       fetchSpendData(date, dateFilterType)
 *     }
 *   }, [isReady, date, dateFilterType])
 *
 *   if (!isReady) return <Loading />
 *   return <SpendContent />
 * }
 * ```
 */
export const useInitializeDateContext = (
  pageId: string
): { isReady: boolean; context: DateContextType } => {
  const dispatch = useAppDispatch()
  const tenant = useAppSelector(selectTenant)
  const isReady = useAppSelector(selectIsActiveContextInitialized)

  // Determine the context type for this page
  const context = getDateContextForPage(pageId)

  // Use useLayoutEffect to ensure this runs synchronously before paint
  // This prevents the component from rendering with stale date values
  useLayoutEffect(() => {
    // 1. Switch to the correct context (this syncs legacy fields too)
    dispatch(setActiveDateContext(context))

    // 2. Initialize the context with defaults if not already done
    // The initializeDateContext reducer is idempotent - safe to call multiple times
    const defaultDate = getDefaultDateForContext(context, tenant)
    const defaultDateFilterType = getDefaultDateFilterTypeForContext(context)

    dispatch(
      initializeDateContext({
        context,
        date: defaultDate,
        dateFilterType: defaultDateFilterType,
      })
    )
  }, [pageId, context, tenant, dispatch])

  return { isReady, context }
}



/**
 * BASE FILTER HOOK
 *
 * For regular pages (Sales, Labor, Speed of Service, Loyalty, Spend, PnL)
 * Provides core filter functionality without comparison or special filters.
 *
 * @param pageId - Optional page identifier. When provided, the hook reads from
 *                 the correct context immediately without waiting for effects.
 *                 This prevents the stale-context issue on initial render.
 *
 * @example
 * ```tsx
 * // Recommended: Pass pageId for immediate correct context
 * const { date, isReady } = useReduxBaseFilter('spend')
 *
 * // Legacy: Without pageId, relies on useInitializeDateContext effect
 * const { date, isReady } = useReduxBaseFilter()
 * ```
 */
export const useReduxBaseFilter = (pageId?: string) => {
  const dispatch = useAppDispatch()

  // Select core filter state
  const selectedBranch = useAppSelector(selectSelectedBranch)
  const selectedBranchType = useAppSelector(selectSelectedBranchType)
  const refreshTrigger = useAppSelector(selectRefreshCallbacks)

  // Determine context: use pageId if provided, otherwise use active context from state
  const expectedContext = pageId ? getDateContextForPage(pageId) : undefined
  const activeContext = useAppSelector(selectActiveDateContext)
  const effectiveContext = expectedContext ?? activeContext

  // Check if initialization is complete AND context matches expected
  const contextInitialized = useAppSelector(selectIsActiveContextInitialized)
  const contextMatches = expectedContext ? activeContext === expectedContext : true
  const isReady = contextInitialized && contextMatches

  // Read date values from the effective context
  // When pageId is provided, this reads from the correct context immediately
  // without waiting for useLayoutEffect to update activeDateContext
  const dateContexts = useAppSelector(selectDateContexts)
  const date = dateContexts[effectiveContext].date
  const dateFilterType = dateContexts[effectiveContext].dateFilterType

  // Action dispatchers wrapped in useCallback for stable references
  const handleSetSelectedBranch = useCallback(
    (branch: string, branchType: BranchType) => {
      dispatch(setSelectedBranch({ branch, branchType }))
    },
    [dispatch]
  )

  const handleSetSelectedBranchType = useCallback(
    (branchType: BranchType) => {
      dispatch(setSelectedBranchType(branchType))
    },
    [dispatch]
  )

  const handleSetDate = useCallback(
    (newDate: DateRange | undefined) => {
      dispatch(setDateForContext({ context: effectiveContext, date: newDate }))
    },
    [dispatch, effectiveContext]
  )

  const handleSetDateFilterType = useCallback(
    (filterType: DateType) => {
      dispatch(
        setDateFilterTypeForContext({
          context: effectiveContext,
          dateFilterType: filterType,
        })
      )
    },
    [dispatch, effectiveContext]
  )

  const handleResetFilters = useCallback(() => {
    dispatch(resetFilters())
  }, [dispatch])

  const handleRefreshData = useCallback(
    (pageId: string) => {
      dispatch(triggerRefresh(pageId))
    },
    [dispatch]
  )

  return {
    // Branch filters
    selectedBranch,
    selectedBranchType,
    setSelectedBranch: handleSetSelectedBranch,
    setSelectedBranchType: handleSetSelectedBranchType,

    // Date filters (from active context)
    date,
    dateFilterType,
    setDate: handleSetDate,
    setDateFilterType: handleSetDateFilterType,

    // Actions
    resetFilters: handleResetFilters,
    refreshData: handleRefreshData,
    refreshTrigger,

    isReady, // True when context is initialized AND matches expected - use to guard data fetching
    activeContext, // The current active context in Redux state
    effectiveContext, // The context being used (from pageId if provided, otherwise activeContext)

}
}

/**
 * COMPARISON FILTER HOOK
 *
 * For lens pages that need comparison branches (PnL Lens, Labor Lens)
 * Includes all base filters plus comparison branch.
 *
 * @param pageId - Optional page identifier. When provided, the hook reads from
 *                 the correct context immediately without waiting for effects.
 *
 * Inherits from useReduxBaseFilter:
 * - `isReady`: boolean - Guard data fetching until context is initialized
 * - `activeContext`: DateContextType - The active date context
 */
export const useReduxComparisonFilter = (pageId?: string) => {
  const baseFilters = useReduxBaseFilter(pageId)
  const dispatch = useAppDispatch()

  // Select comparison-specific state
  const comparisonBranch = useAppSelector(selectComparisonBranch) || ''

  const handleSetComparisonBranch = useCallback(
    (branch: string) => {
      dispatch(setComparisonBranch(branch))
    },
    [dispatch]
  )

  return {
    ...baseFilters,
    comparisonBranch,
    setComparisonBranch: handleSetComparisonBranch,
  }
}

/**
 * SALES LENS FILTER HOOK
 *
 * For sales-lens pages with temperature and size filters.
 * Includes base filters plus sales-specific filters.
 *
 * @param pageId - Optional page identifier. When provided, the hook reads from
 *                 the correct context immediately without waiting for effects.
 *
 * Inherits from useReduxBaseFilter:
 * - `isReady`: boolean - Guard data fetching until context is initialized
 * - `activeContext`: DateContextType - The active date context
 */
export const useReduxSalesLensFilter = (pageId?: string) => {
  const baseFilters = useReduxBaseFilter(pageId)
  const dispatch = useAppDispatch()

  // Select sales-lens specific state
  const temperatureFilter = useAppSelector(selectTemperatureFilter) || 'all'
  const sizeFilter = useAppSelector(selectSizeFilter) || 'all'

  const handleSetTemperatureFilter = useCallback(
    (filter: string) => {
      dispatch(setTemperatureFilter(filter))
    },
    [dispatch]
  )

  const handleSetSizeFilter = useCallback(
    (filter: string) => {
      dispatch(setSizeFilter(filter))
    },
    [dispatch]
  )

  return {
    ...baseFilters,
    temperatureFilter,
    sizeFilter,
    setTemperatureFilter: handleSetTemperatureFilter,
    setSizeFilter: handleSetSizeFilter,
  }
}

/**
 * METRIC FILTER HOOK
 *
 * For lens pages with metric/chart type filters.
 * Includes comparison filters plus metric filters.
 *
 * @param pageId - Optional page identifier. When provided, the hook reads from
 *                 the correct context immediately without waiting for effects.
 *
 * Inherits from useReduxComparisonFilter (which inherits from useReduxBaseFilter):
 * - `isReady`: boolean - Guard data fetching until context is initialized
 * - `activeContext`: DateContextType - The active date context
 */
export const useReduxMetricFilter = (pageId?: string) => {
  const comparisonFilters = useReduxComparisonFilter(pageId)
  const dispatch = useAppDispatch()

  const typeFilter = useAppSelector(selectTypeFilter) || ''
  const chartType = useAppSelector(selectChartType) || ''

  const handleSetTypeFilter = useCallback(
    (filter: string) => {
      dispatch(setTypeFilter(filter))
    },
    [dispatch]
  )

  const handleSetChartType = useCallback(
    (type: string) => {
      dispatch(setChartType(type))
    },
    [dispatch]
  )

  return {
    ...comparisonFilters,
    typeFilter,
    chartType,
    setTypeFilter: handleSetTypeFilter,
    setChartType: handleSetChartType,
  }
}

/**
 * GENERIC FILTER SELECTOR
 *
 * Direct access to filter state without actions
 * Useful when you only need to read values, not update them
 */
export const useFilterState = () => {
  return useAppSelector(selectFilterState)
}

/**
 * Hook to watch for refresh triggers for a specific page
 *
 * @param pageId - The ID of the page to watch
 * @returns boolean - Toggles when refresh is requested
 */
export const usePageRefreshTrigger = (pageId: string) => {
  return useAppSelector(selectPageRefreshTrigger(pageId))
}
